import { type ElementType } from 'react'
import moment from 'moment'
import { type FormikProps } from 'formik'
import { ReportFilterTypeEnum } from '__codegen__/api'
import { type DashMessages } from 'intl/intl.types'
import {
  DEFAULT_DATE_RANGE_OPTIONS,
  PAST_DATE_RANGE_OPTIONS,
} from 'legacy/common/utils/time/dateRanges'
import { DateSelectRange } from 'legacy/dash-components/date-select-range/DateSelectRange'
import { RetailerLocationSelect } from 'legacy/dash-components/retailer-location-select/RetailerLocationSelect'
import {
  type GeneratedReportsReportFilter,
  type GeneratedReportsFormKey,
  type GeneratedReportsReportFilterValue,
} from 'legacy/sections/generated-reports/components/add/components/form/filters/utils/generatedReportsAddFormFilters.types'
import {
  type GeneratedReportsAddFormValues,
  type GeneratedReportsAddFormFilterValues,
} from 'legacy/sections/generated-reports/components/add/components/form/utils/generatedReportsAddForm.types'
import { type DateSelectRangeValue } from 'legacy/dash-components/date-select-range/utils/dateSelectRange.hooks'
import { errors } from 'utils/error-handling/errors'

export const DATE_RANGE_TEST_ID = 'generated-reports-add-form-filter-date-range'
export const RETAILER_LOCATION_TEST_ID = 'generated-reports-add-form-filter-retailer-location'

const throwUnexpectedReportFilterError = (filterType: string): never => {
  errors.captureMessage(`Encountered unknown Generated Report filter: ${filterType}.`)

  throw new Error(`Encountered unknown Generated Report filter: ${filterType}.`)
}

export const generatedReportsFilterFormKeyMapping = ({
  filterType,
}: GeneratedReportsReportFilter): GeneratedReportsFormKey => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      return 'dateRange'
    case ReportFilterTypeEnum.RetailerLocations:
      return 'retailerLocation'
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}

export const generatedReportsFilterInitialValueMapping = ({
  filterType,
  minDate,
  maxDate,
}: GeneratedReportsReportFilter): GeneratedReportsReportFilterValue => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      return {
        dateRange: 'custom',
        customDates: {
          startDate: moment(minDate),
          endDate: moment(maxDate),
        },
      }
    case ReportFilterTypeEnum.RetailerLocations:
      return {
        retailerLocationId: null,
        storeGroupId: null,
      }
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}

export const generatedReportsFilterCheckValidator = (
  { filterType }: GeneratedReportsReportFilter,
  { dateRange, retailerLocation }: GeneratedReportsAddFormValues
): boolean => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      if (dateRange) {
        if (dateRange.dateRange) {
          if (dateRange.dateRange === 'custom') {
            return (
              !!dateRange.customDates &&
              !!dateRange.customDates.startDate &&
              !!dateRange.customDates.endDate
            )
          }
          return true
        }
      }
      return false
    case ReportFilterTypeEnum.RetailerLocations:
      return !!retailerLocation
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}

export const generatedReportsFilterErrorMessages: {
  [key in GeneratedReportsFormKey]: DashMessages
} = {
  dateRange: 'generatedReports.newReport.formFields.dateRange.error.required',
  retailerLocation: 'generatedReports.newReport.formFields.retailerLocation.error.required',
}

export const generatedReportsFilterLabelMapping = ({
  filterType,
}: GeneratedReportsReportFilter): DashMessages => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      return 'generatedReports.newReport.formFields.dateRange.label'
    case ReportFilterTypeEnum.RetailerLocations:
      return 'generatedReports.newReport.formFields.retailerLocation.label'
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}

export const generatedReportsFilterPlaceholderMapping = ({
  filterType,
}: GeneratedReportsReportFilter): DashMessages => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      return 'generatedReports.newReport.formFields.dateRange.placeholder'
    case ReportFilterTypeEnum.RetailerLocations:
      return 'generatedReports.newReport.formFields.retailerLocation.placeholder'
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}

export const generatedReportsFilterComponentMapping = ({
  filterType,
}: GeneratedReportsReportFilter): ElementType | null => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      return DateSelectRange
    case ReportFilterTypeEnum.RetailerLocations:
      return RetailerLocationSelect
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}

export const generatedReportsFilterComponentFormikPropsMapping = (
  { filterType }: GeneratedReportsReportFilter,
  {
    setFieldValue,
    setFieldTouched,
    values,
    touched,
    errors,
    isSubmitting,
  }: FormikProps<GeneratedReportsAddFormValues>
) => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      return {
        onChange: (e: DateSelectRangeValue) => setFieldValue('dateRange', e),
        onBlur: () => setFieldTouched('dateRange'),
        value: values.dateRange,
        hasError: !isSubmitting && touched.dateRange && !!errors.dateRange,
      }
    case ReportFilterTypeEnum.RetailerLocations:
      return {
        onChange: (e: GeneratedReportsAddFormFilterValues['retailerLocation']) =>
          setFieldValue('retailerLocation', e),
        onBlur: () => setFieldTouched('retailerLocation'),
        value: values.retailerLocation,
        hasError: !isSubmitting && touched.retailerLocation && !!errors.retailerLocation,
      }
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}

export const generatedReportsFilterComponentPropsMapping = ({
  filterType,
  minDate,
  maxDate,
}: GeneratedReportsReportFilter) => {
  switch (filterType) {
    case ReportFilterTypeEnum.DateRange:
      if (moment(maxDate).isBefore(moment(), 'day')) {
        return {
          dateRangeOptions: PAST_DATE_RANGE_OPTIONS,
          minDate: moment(minDate).startOf('day'),
          maxDate: moment(maxDate).endOf('day'),
          testId: DATE_RANGE_TEST_ID,
        }
      }

      return {
        dateRangeOptions: DEFAULT_DATE_RANGE_OPTIONS,
        minDate: moment(minDate).startOf('day'),
        maxDate: moment(maxDate).endOf('day'),
        testId: DATE_RANGE_TEST_ID,
      }
    case ReportFilterTypeEnum.RetailerLocations:
      return {
        testId: RETAILER_LOCATION_TEST_ID,
      }
    default:
      return throwUnexpectedReportFilterError(filterType)
  }
}
